import * as React from 'react';
import { Avatar, Empty, Input, Spin, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useParams } from 'react-router-dom';
import { useCampaignAvatarMap, useCampaignPerformance, useCampaignPosts } from '../queryHooks';
import { formatCompact, formatPct, formatVIT, PLATFORM_LABEL } from '../format';
import type { CampaignPost, CreatorPerformanceRow } from '../types';

const postEngagements = (p: CampaignPost) => p.likes + p.comments + p.shares + p.saves;

const postEstReach = (p: CampaignPost) => {
    if (p.impressions > 0) return p.impressions;
    if (p.reach > 0) return p.reach;
    return 0;
};

const postLabel = (p: CampaignPost) => {
    const platform = PLATFORM_LABEL[p.platform] || p.platform?.toUpperCase() || 'Post';
    const type = p.content_type ? p.content_type.replace(/_/g, ' ') : 'post';
    const when = p.published_at ? dayjs(p.published_at).format('MMM D, YYYY') : '';
    const handle = p.author_username ? `@${p.author_username}` : '';
    return { platform, type, when, handle };
};

const CreatorPostBreakdown: React.FC<{ campaignId: number; creatorId: number }> = ({ campaignId, creatorId }) => {
    const { data: posts = [], isLoading } = useCampaignPosts(campaignId, {
        status: 'published',
        in_campaign: 'true',
        creator_id: String(creatorId),
        sort: 'recent',
    });

    if (isLoading) {
        return (
            <div style={{ padding: '12px 48px' }}>
                <Spin size="small" />
            </div>
        );
    }

    if (posts.length === 0) {
        return (
            <div style={{ padding: '8px 48px' }}>
                <Typography.Text type="secondary">No in-campaign posts for this creator.</Typography.Text>
            </div>
        );
    }

    const columns: ColumnsType<CampaignPost> = [
        {
            title: 'Post',
            key: 'post',
            render: (_, p) => {
                const { platform, type, when, handle } = postLabel(p);
                const caption = (p.caption || '').trim();
                const preview = caption.length > 60 ? `${caption.slice(0, 60)}…` : caption;
                return (
                    <div style={{ maxWidth: 320 }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', marginBottom: 4 }}>
                            <Tag style={{ margin: 0 }}>{platform}</Tag>
                            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                {[handle, type, when].filter(Boolean).join(' · ')}
                            </Typography.Text>
                        </div>
                        {p.post_url ? (
                            <a href={p.post_url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                                {preview || 'Open post'}
                            </a>
                        ) : (
                            <Typography.Text type="secondary">{preview || '—'}</Typography.Text>
                        )}
                    </div>
                );
            },
        },
        { title: 'VIT', dataIndex: 'vit', render: (v: number) => formatVIT(v) },
        {
            title: 'Posts',
            key: 'posts',
            width: 72,
            render: () => 1,
        },
        {
            title: 'Engagements',
            key: 'engagements',
            render: (_, p) => formatCompact(postEngagements(p)),
        },
        {
            title: 'Engagement rate',
            dataIndex: 'engagement_rate',
            render: (v: number) => formatPct(v),
        },
        {
            title: 'Video views',
            dataIndex: 'views',
            render: (v: number) => formatCompact(v),
        },
        {
            title: 'Est. impressions',
            key: 'est_impressions',
            render: (_, p) => {
                const n = postEstReach(p);
                return n > 0 ? formatCompact(n) : <span style={{ color: 'var(--text-faint)' }}>--</span>;
            },
        },
        {
            title: 'Potential reach',
            key: 'potential_reach',
            render: (_, p) => {
                const n = postEstReach(p);
                return n > 0 ? formatCompact(n) : <span style={{ color: 'var(--text-faint)' }}>--</span>;
            },
        },
        { title: 'Organic engagements', render: () => <span style={{ color: 'var(--text-faint)' }}>--</span> },
        { title: 'Paid engagements', render: () => <span style={{ color: 'var(--text-faint)' }}>--</span> },
    ];

    return (
        <Table
            rowKey="id"
            dataSource={posts}
            columns={columns}
            pagination={false}
            size="small"
            showHeader
            style={{ margin: '4px 0 8px' }}
        />
    );
};

const PerformanceSection: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const campaignId = id ? Number(id) : undefined;
    const { data, isLoading } = useCampaignPerformance(campaignId);
    const avatarById = useCampaignAvatarMap(campaignId);
    const [search, setSearch] = React.useState('');
    const [expandedRowKeys, setExpandedRowKeys] = React.useState<React.Key[]>([]);

    if (isLoading) return <div style={{ padding: 32, textAlign: 'center' }}><Spin /></div>;
    const rows = (data?.creators ?? []).filter((r) =>
        !search.trim() || r.name.toLowerCase().includes(search.trim().toLowerCase()),
    );

    if (!data) return <Empty />;

    const columns: ColumnsType<CreatorPerformanceRow> = [
        {
            title: 'Name',
            dataIndex: 'name',
            render: (name: string, row) => (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <Avatar src={avatarById[row.creator_id] ? `data:image/jpeg;base64,${avatarById[row.creator_id]}` : undefined} size={28}>
                        {name?.[0]}
                    </Avatar>
                    {name}
                </span>
            ),
        },
        { title: 'VIT', dataIndex: 'vit', render: (v: number) => formatVIT(v) },
        { title: 'Posts', dataIndex: 'posts' },
        { title: 'Engagements', dataIndex: 'engagements', render: (v: number) => formatCompact(v) },
        { title: 'Engagement rate', dataIndex: 'engagement_rate', render: (v: number) => formatPct(v) },
        { title: 'Video views', dataIndex: 'video_views', render: (v: number) => formatCompact(v) },
        { title: 'Est. impressions', dataIndex: 'est_impressions', render: (v: number) => formatCompact(v) },
        { title: 'Potential reach', dataIndex: 'potential_reach', render: (v: number) => formatCompact(v) },
        { title: 'Organic engagements', render: () => <span style={{ color: 'var(--text-faint)' }}>--</span> },
        { title: 'Paid engagements', render: () => <span style={{ color: 'var(--text-faint)' }}>--</span> },
    ];

    return (
        <div>
            <Input.Search
                allowClear
                placeholder="Search for creator"
                style={{ width: 280, marginBottom: 8 }}
                onChange={(e) => setSearch(e.target.value)}
            />
            <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 16, fontSize: 12 }}>
                Click a creator row to expand post-level stats (in-campaign published posts only).
            </Typography.Text>
            <Table
                rowKey="creator_id"
                dataSource={rows}
                pagination={false}
                size="middle"
                scroll={{ x: 'max-content' }}
                columns={columns}
                expandable={{
                    expandedRowKeys,
                    onExpandedRowsChange: (keys) => setExpandedRowKeys(keys),
                    expandRowByClick: true,
                    rowExpandable: (row) => row.posts > 0,
                    expandedRowRender: (row) =>
                        campaignId ? (
                            <CreatorPostBreakdown campaignId={campaignId} creatorId={row.creator_id} />
                        ) : null,
                }}
                onRow={(row) => ({
                    style: { cursor: row.posts > 0 ? 'pointer' : undefined },
                })}
                summary={() => (
                    <Table.Summary.Row>
                        <Table.Summary.Cell index={0} colSpan={2}>
                            <Typography.Text strong>{data.creators.length} creators</Typography.Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={2}>{formatVIT(data.totals.vit)}</Table.Summary.Cell>
                        <Table.Summary.Cell index={3}>{data.totals.posts}</Table.Summary.Cell>
                        <Table.Summary.Cell index={4}>{formatCompact(data.totals.engagements)}</Table.Summary.Cell>
                        <Table.Summary.Cell index={5}>{formatPct(data.totals.engagement_rate)}</Table.Summary.Cell>
                        <Table.Summary.Cell index={6}>{formatCompact(data.totals.video_views)}</Table.Summary.Cell>
                        <Table.Summary.Cell index={7}>{formatCompact(data.totals.est_impressions)}</Table.Summary.Cell>
                        <Table.Summary.Cell index={8}>{formatCompact(data.totals.potential_reach)}</Table.Summary.Cell>
                        <Table.Summary.Cell index={9}>--</Table.Summary.Cell>
                        <Table.Summary.Cell index={10}>--</Table.Summary.Cell>
                    </Table.Summary.Row>
                )}
            />
        </div>
    );
};

export default PerformanceSection;
